import { useVirtualizer, type VirtualItem, type Virtualizer } from '@tanstack/react-virtual';
import { type MutableRefObject, useEffect } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { WORKSPACE_AGENT_MESSAGE_ROW_PADDING_START_PX } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentMessageRowLayout';
import {
  EMPTY_WORKSPACE_AGENT_MESSAGE_ROW_PILLS,
  type WorkspaceAgentMessageRowPills,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentMessageRowPills';
import { estimateWorkspaceAgentMessageRowSize } from '~/components/v2/components/workspace/workspace-agent-panel/utils/estimateWorkspaceAgentMessageRowSize';
import { type WorkspaceAgentDisplayRow } from '~/components/v2/components/workspace/workspace-agent-panel/utils/groupWorkspaceAgentMessagesForDisplay';
import {
  getIsWorkspaceAgentScrolledToBottom,
  getWorkspaceAgentScrollContainerMetrics,
  pinWorkspaceAgentScrollToBottom,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/pinWorkspaceAgentScrollToBottom';

const WORKSPACE_AGENT_MESSAGE_VIRTUALIZER_OVERSCAN = 5;

type EstimateWorkspaceAgentVirtualRowSizeParams = {
  index: number;
  pillsByRowId: ReadonlyMap<string, WorkspaceAgentMessageRowPills>;
  rows: WorkspaceAgentDisplayRow[];
};

const estimateWorkspaceAgentVirtualRowSize = ({
  index,
  pillsByRowId,
  rows,
}: EstimateWorkspaceAgentVirtualRowSizeParams): number => {
  const row = rows[index];

  if (!row) {
    return 0;
  }

  return estimateWorkspaceAgentMessageRowSize(row, pillsByRowId.get(row.id) ?? EMPTY_WORKSPACE_AGENT_MESSAGE_ROW_PILLS);
};

type PinVirtualizedAgentChatToBottomParams = {
  isPinnedToBottomRef: MutableRefObject<boolean | undefined>;
  isSmoothProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  scrollContainerRef: MutableRefObject<HTMLElement | null>;
};

/**
 * `scrollToIndex(..., { align: 'end' })` aims at the last virtual row, then retries on a timeout
 * once that row is measured. Pin the scrollport itself so a slow measure pass cannot stop short.
 */
const pinVirtualizedAgentChatToBottom = ({
  isPinnedToBottomRef,
  isSmoothProgrammaticPinRef,
  scrollContainerRef,
}: PinVirtualizedAgentChatToBottomParams) => {
  const container = scrollContainerRef.current;

  if (!container) {
    return;
  }

  if (isPinnedToBottomRef.current === false) {
    return;
  }

  if (isSmoothProgrammaticPinRef.current === true) {
    return;
  }

  const metrics = getWorkspaceAgentScrollContainerMetrics(container);

  if (!getIsWorkspaceAgentScrolledToBottom(metrics)) {
    pinWorkspaceAgentScrollToBottom(container, 'instant');
  }
};

export type UseWorkspaceAgentMessageListVirtualizerParams = {
  catchUpPinAfterSmoothRef: MutableRefObject<(() => void) | undefined>;
  /** While true, skip measure-driven tail pins (jump smooth scroll in flight). */
  suppressMeasureTailPinRef: MutableRefObject<boolean | undefined>;
  isPinnedToBottomRef: MutableRefObject<boolean | undefined>;
  isSmoothProgrammaticPinRef: MutableRefObject<boolean | undefined>;
  pillsByRowId: ReadonlyMap<string, WorkspaceAgentMessageRowPills>;
  rows: WorkspaceAgentDisplayRow[];
  scrollContainerRef: MutableRefObject<HTMLElement | null>;
  /** Offset of this list from the top of the scroll parent, so a prefix above it stays in range. */
  scrollMargin: number;
  sessionId: string | null;
};

export type UseWorkspaceAgentMessageListVirtualizerResult = {
  totalSize: number;
  virtualItems: VirtualItem<Element>[];
  virtualizer: Virtualizer<HTMLElement, Element>;
};

export const useWorkspaceAgentMessageListVirtualizer = ({
  catchUpPinAfterSmoothRef,
  suppressMeasureTailPinRef,
  isPinnedToBottomRef,
  isSmoothProgrammaticPinRef,
  pillsByRowId,
  rows,
  scrollContainerRef,
  scrollMargin,
  sessionId,
}: UseWorkspaceAgentMessageListVirtualizerParams): UseWorkspaceAgentMessageListVirtualizerResult => {
  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: (index) => estimateWorkspaceAgentVirtualRowSize({ index, pillsByRowId, rows }),
    getItemKey: (index) => rows[index]?.id ?? index,
    getScrollElement: () => scrollContainerRef.current,
    overscan: WORKSPACE_AGENT_MESSAGE_VIRTUALIZER_OVERSCAN,
    paddingStart: WORKSPACE_AGENT_MESSAGE_ROW_PADDING_START_PX,
    scrollMargin,
  });

  // Instance field in @tanstack/react-virtual 3.8.3 — not a VirtualizerOptions key yet.
  virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (item) => {
    if (suppressMeasureTailPinRef.current === true) {
      return false;
    }

    if (isPinnedToBottomRef.current !== false) {
      return false;
    }

    const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;

    return item.start < scrollTop;
  };

  const totalSize = virtualizer.getTotalSize();
  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    catchUpPinAfterSmoothRef.current = () => {
      if (rows.length === 0 || isPinnedToBottomRef.current === false) {
        return;
      }

      pinVirtualizedAgentChatToBottom({
        isPinnedToBottomRef,
        isSmoothProgrammaticPinRef,
        scrollContainerRef,
      });
    };

    return () => {
      catchUpPinAfterSmoothRef.current = undefined;
    };
  }, [catchUpPinAfterSmoothRef, isPinnedToBottomRef, isSmoothProgrammaticPinRef, rows.length, scrollContainerRef]);

  // Estimated → measured corrections (and streaming growth) land after the sizer's scrollHeight
  // is known — match the auto-scroll hook's two-frame follow-up so a slow measure pass still
  // re-pins before Cypress (or a reader) samples scrollTop.
  useIsomorphicLayoutEffect(() => {
    if (rows.length === 0 || isPinnedToBottomRef.current !== true || suppressMeasureTailPinRef.current === true) {
      return;
    }

    let cancelled = false;
    let followUpFrameId = 0;

    const pinAfterMeasure = () => {
      pinVirtualizedAgentChatToBottom({
        isPinnedToBottomRef,
        isSmoothProgrammaticPinRef,
        scrollContainerRef,
      });
    };

    followUpFrameId = requestAnimationFrame(() => {
      if (cancelled) {
        return;
      }

      pinAfterMeasure();
      followUpFrameId = requestAnimationFrame(() => {
        if (!cancelled) {
          pinAfterMeasure();
        }
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(followUpFrameId);
    };
  }, [isPinnedToBottomRef, rows.length, sessionId, suppressMeasureTailPinRef, totalSize]);

  return { totalSize, virtualItems, virtualizer };
};
